<template>
  <v-card flat xs12>
    <v-layout class="align-star mb-3">
      <v-text-field append-icon="search" :placeholder="$t('private_global.filter')" single-line hide-details v-model="search"></v-text-field>
    </v-layout>

    <v-data-table
      :items="invitations"
      :headers="computedHeaders"
      :rows-per-page-items="[10, 25, 50]"
      :rows-per-page-text="paginationText"
      :total-items="totalEvents"
      :pagination.sync="pagination"
      :loading="isLoading"
      :hide-headers="totalEvents == 0"
      :hide-actions="totalEvents == 0"
      expand
    >
      <template slot="items" slot-scope="props">
        <tr style="cursor: pointer;" :class="{ 'wsp-no-border': props.expanded }" @click="props.expanded = !props.expanded">
          <td>{{ props.item.email }}</td>
          <td>{{ props.item.keypad }}</td>
          <td>{{ props.item.code }}</td>
          <td v-if="!$vuetify.breakpoint.mdAndDown">{{ props.item.start | formatDate }}</td>
          <td v-if="!$vuetify.breakpoint.mdAndDown">{{ noDateText(props.item.end) }}</td>
          <td style="width:70px">
            <v-icon class="right" v-if="props.expanded">keyboard_arrow_up</v-icon>
            <v-icon class="right" v-else>keyboard_arrow_down</v-icon>
          </td>
        </tr>
      </template>

      <template slot="expand" slot-scope="props">
        <v-card pa-2 :key="props.id" flat>
          <v-layout wrap row pl-5 pt-3>
            <v-flex xs12>
              <div class="pb-1" v-if="props.item.code">
                <strong>{{ $t('private_global_invitationCode') }}:</strong>
                {{ props.item.code }}
              </div>
              <div class="pb-1" v-if="props.item.name">
                <strong>{{ $t('private_global_name') }}:</strong>
                {{ props.item.name }}
              </div>
              <div class="pb-1" v-if="props.item.keypad">
                <strong>{{ $t('private_global_keypad') }}:</strong>
                {{ props.item.keypad }}
              </div>
              <div class="pb-1" v-if="props.item.email">
                <strong>{{ $t('private_global_email') }}:</strong>
                {{ props.item.email }}
              </div>
              <div class="pb-1" v-if="props.item.description">
                <strong>{{ $t('private_global_description') }}:</strong>
                {{ props.item.description }}
              </div>
              <div class="pb-1" v-if="$vuetify.breakpoint.mdAndDown">
                <strong>{{ $t('private_global_start') }}:</strong>
                {{ props.item.start | formatDate }}
              </div>
              <div class="pb-1" v-if="$vuetify.breakpoint.mdAndDown">
                <strong>{{ $t('private_global_end') }}:</strong>
                {{ noDateText(props.item.end) }}
              </div>
              <div class="pb-1" v-if="props.item.spot">
                <strong>{{ $t('private_global_spot') }}:</strong>
                {{ props.item.spot }}
              </div>
              <div class="right mb-2 mr-3">
                <v-btn flat small color="error" dark @click="deleteInvitation(props.item)">{{ $t('global_button_remove') }}</v-btn>
                <v-btn flat small color="primary" dark @click="resendInvitation(props.item)">{{ $t('global_button_resend') }}</v-btn>
              </div>
            </v-flex>
          </v-layout>
        </v-card>
      </template>

      <template slot="no-data">
        <v-alert :value="true" outline color="info" class="mt-3" v-html="$t('private_dash_table_earnings_nodata')"> </v-alert>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import store from '@/store';
import _ from 'lodash';
import keys from '@/store/keys';
import { EventBus } from '@/services/eventBus';
import tenantService from '@/services/tenantService';

export default {
  name: 'TenantInvitations',
  components: {},
  computed: {
    // @see: https://codepen.io/anon/pen/bMQGbm
    computedHeaders() {
      return this.headers.filter(h => !h.hide || !this.$vuetify.breakpoint[h.hide]);
    }
  },
  watch: {
    pagination: {
      handler() {
        this.queryChange();
      },
      deep: true
    },
    search: function(newValue) {
      let l = newValue.trim().length;
      if (l > 3 || l === 0) {
        this.debounceSearch();
      }
    }
  },
  data() {
    return {
      debounceSearch: {},
      invitations: [],
      totalEvents: 0,
      search: '',
      paginationText: '',
      isLoading: false,
      pagination: {
        rowsPerPage: 10,
        descending: true,
        sortBy: 'id'
      },
      headers: [
        {
          i18nText: 'private_global_email',
          text: this.$i18n.t('private_global_email'),
          align: 'left',
          value: 'email'
        },
        {
          i18nText: 'private_global_keypad',
          text: this.$i18n.t('private_global_keypad'),
          align: 'left',
          value: 'keypad',
          sortable: false
        },
        {
          i18nText: 'private_global_invitationCode',
          text: this.$i18n.t('private_global_invitationCode'),
          align: 'left',
          value: 'code'
        },
        {
          i18nText: 'private_global_start',
          text: this.$i18n.t('private_global_start'),
          align: 'left',
          value: 'start',
          hide: 'mdAndDown',
          sortable: false
        },
        {
          i18nText: 'private_global_end',
          text: this.$i18n.t('private_global_end'),
          align: 'left',
          value: 'end',
          hide: 'mdAndDown',
          sortable: false
        },
        { value: '' } // actions column
      ]
    };
  },
  created() {
    this.debounceSearch = _.debounce(this.queryChange, 500);
    EventBus.$on(keys.eventOnTenantInvitationCreated, this.loadDataWithDelay);
  },

  destroyed() {
    EventBus.$off(keys.eventOnTenantInvitationCreated);
  },

  methods: {
    async loadData(params) {
      let resp = await tenantService.invitations(params);

      if (resp.error) {
      } else {
        this.invitations = resp.data.data;
        this.totalEvents = resp.data.recordsTotal;
      }
      this.isLoading = false;
    },

    async loadDataWithDelay(params) {
      setTimeout(() => {
        this.queryChange();
      }, 250);
    },

    queryChange() {
      this.isLoading = true;

      const { sortBy, descending, page, rowsPerPage } = this.pagination;
      let queryInfo = {
        pageSize: rowsPerPage,
        page: page,
        sortInfo: {
          column: {
            sortBy: sortBy,
            order: descending ? 'descending' : 'ascending'
          }
        }
      };

      let params = {
        parkingId: this.$route.params.id,
        length: queryInfo.pageSize,
        start: (queryInfo.page - 1) * queryInfo.pageSize,
        lang: store.state.langSelected.langWsp,
        term: this.search,
        sortBy: sortBy,
        descending: descending
      };

      this.loadData(params);
    },

    noDateText(v) {
      let date = this.$options.filters.formatDate(v);
      if (date === '') {
        return this.$i18n.t('global.noEndDate');
      }
      return date;
    },

    async resendInvitation(item) {
      let payload = {
        parkingId: Number(this.$route.params.id),
        id: item.id
      };
      let resp = await tenantService.resendInvitation(payload);
      if (resp.error) {
      } else {
        EventBus.$emit(keys.eventOnShowNotification, {
          title: '',
          message: this.$i18n.t('tenant.resendInvitation.toast'),
          type: 'info',
          doNotTranslate: true
        });
      }
      this.isLoading = false;
    },

    async deleteInvitation(item) {
      let payload = {
        parkingId: Number(this.$route.params.id),
        id: item.id
      };

      let resp = await tenantService.deleteInvitation(payload);
      if (resp.error) {
      } else {
        this.queryChange();

        EventBus.$emit(keys.eventOnShowNotification, {
          title: '',
          message: this.$i18n.t('tenant.deleteInvitation.toast'),
          type: 'info',
          doNotTranslate: true
        });
      }
      this.isLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/parking/TenantInvitations.vue